import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import PageLayout from '../../components/layout/PageLayout';
import api from '../../api/config';
import { CALL_RECORDS } from '../../api/endpoints';
import { getLeads } from '../../store/slices/leadsSlice';
import { extractList } from '../../utils/response';
import { getErrorMessage } from '../../utils/errorHandler';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { CALL_RECORDS_LIST_PATH } from '../../navigations/path';
import { buildLeadsAutocompleteParams } from '../../components/Leads/utils';
import { normalizeLeadOption, loadCallRecordStatuses } from './callRecordFormMeta';
import CallRecordingUploadSection from './CallRecordingUploadSection';
import {
  uploadRecordingAttachmentFileChunked,
  extractAttachmentIdFromUploadResponse,
} from './callRecordAttachmentUpload';
import { getRecordingsList } from './callRecordHelpers';

const multipartHeaders = { headers: { 'Content-Type': 'multipart/form-data' } };

const EditCallRecord = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [leadInput, setLeadInput] = useState('');
  const [leadOptions, setLeadOptions] = useState([]);
  const [leadLoading, setLeadLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [statusId, setStatusId] = useState('');
  const [note, setNote] = useState('');
  const [file, setFile] = useState(null);
  const [replacementAttachmentId, setReplacementAttachmentId] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [existingRecordings, setExistingRecordings] = useState([]);

  const fetchLeads = useCallback(
    async (searchTerm) => {
      setLeadLoading(true);
      try {
        const params = buildLeadsAutocompleteParams({
          search: searchTerm,
          page: 1,
          perPage: 100,
        });
        const res = await dispatch(getLeads(params)).unwrap();
        const raw = extractList(res);
        const list = (Array.isArray(raw) ? raw : []).map(normalizeLeadOption).filter(Boolean);
        setLeadOptions(list);
      } catch (err) {
        toast.error(getErrorMessage(err) || 'Failed to load leads');
        setLeadOptions([]);
      } finally {
        setLeadLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    const t = setTimeout(() => fetchLeads(leadInput), 350);
    return () => clearTimeout(t);
  }, [leadInput, fetchLeads]);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true);
      try {
        const [statusList, itemRes] = await Promise.all([
          loadCallRecordStatuses(dispatch),
          api.get(CALL_RECORDS.BY_ID(id), {
            params: { 'relations[]': ['lead', 'closer', 'status', 'recordings'] },
          }),
        ]);
        setStatusOptions(statusList);
        const item = itemRes.data?.data ?? itemRes.data ?? {};
        setExistingRecordings(getRecordingsList(item));
        if (item.lead) {
          const nl = normalizeLeadOption(item.lead);
          setSelectedLead(nl);
          setLeadOptions(nl ? [nl] : []);
        }
        const sid = item.status?.id ?? item.status_id ?? item.status?.uuid ?? '';
        setStatusId(sid != null && sid !== '' ? String(sid) : '');
        setNote(item.note ?? '');
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load recording');
        navigate(CALL_RECORDS_LIST_PATH);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, navigate, dispatch]);

  const handleReplacementFile = async (nextFile) => {
    if (!nextFile) {
      setFile(null);
      setReplacementAttachmentId(null);
      return;
    }
    setUploadingAttachment(true);
    setFile(nextFile);
    setReplacementAttachmentId(null);
    try {
      const uploadBody = await uploadRecordingAttachmentFileChunked(nextFile, { modelId: id });
      const attachmentId = extractAttachmentIdFromUploadResponse(uploadBody);
      if (!attachmentId) {
        toast.error('Attachment upload succeeded but no id was returned.');
        setFile(null);
        return;
      }
      setReplacementAttachmentId(String(attachmentId));
      toast.success('New recording uploaded — click Save to update.');
    } catch (err) {
      toast.error(getErrorMessage(err) || err.response?.data?.message || 'Attachment upload failed');
      setFile(null);
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleRemoveReplacement = () => {
    setFile(null);
    setReplacementAttachmentId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const leadKey = selectedLead?.id ?? selectedLead?.uuid;
    if (!leadKey) {
      toast.warn('Select a lead');
      return;
    }
    if (!statusId) {
      toast.warn('Select a status');
      return;
    }
    if (file && !replacementAttachmentId) {
      toast.warn('Wait for the attachment upload to finish, or remove the file.');
      return;
    }
    if (uploadingAttachment) return;

    setSubmitting(true);
    try {
      if (file && replacementAttachmentId) {
        const fd = new FormData();
        fd.append('lead_id', String(leadKey));
        fd.append('status_id', String(statusId));
        fd.append('note', note || '');
        fd.append('recordings[0]', String(replacementAttachmentId));
        await api.put(CALL_RECORDS.BY_ID(id), fd, multipartHeaders);
      } else {
        await api.put(CALL_RECORDS.BY_ID(id), {
          lead_id: leadKey,
          status_id: statusId,
          note: note || '',
        });
      }
      toast.success('Recording updated.');
      navigate(CALL_RECORDS_LIST_PATH);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update recording');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <Container maxWidth="md" sx={{ py: 3, display: 'flex', justifyContent: 'center', minHeight: '40vh' }}>
          <CircularProgress />
        </Container>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
          Edit recording
        </Typography>

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  options={leadOptions}
                  loading={leadLoading}
                  filterOptions={(x) => x}
                  getOptionLabel={(opt) => opt?.label || opt?.name || opt?.email || ''}
                  isOptionEqualToValue={(a, b) => (a?.id ?? a?.uuid) === (b?.id ?? b?.uuid)}
                  value={selectedLead}
                  onChange={(_, v) => setSelectedLead(v)}
                  onInputChange={(_, v) => setLeadInput(v)}
                  renderInput={(params) => <TextField {...params} label="Lead" required />}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Status</InputLabel>
                  <Select label="Status" value={statusId} onChange={(e) => setStatusId(e.target.value)}>
                    {statusOptions.map((s) => {
                      const val = s.id ?? s.uuid;
                      return (
                        <MenuItem key={String(val)} value={String(val)}>
                          {s.name ?? s.title ?? s.label ?? String(val)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Note"
                  multiline
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <CallRecordingUploadSection
          file={file}
          attachmentId={replacementAttachmentId}
          existingRecordings={existingRecordings}
          uploadingAttachment={uploadingAttachment}
          saving={submitting}
          onFileChange={(f) => {
            void handleReplacementFile(f);
          }}
          onRemove={handleRemoveReplacement}
          disabled={submitting}
          optionalReplace
        />

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
          <Button
            type="button"
            variant="contained"
            disabled={submitting || uploadingAttachment || (Boolean(file) && !replacementAttachmentId)}
            onClick={handleSubmit}
          >
            {submitting ? <CircularProgress size={22} /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" onClick={() => navigate(CALL_RECORDS_LIST_PATH)}>
            Cancel
          </Button>
        </Box>
      </Container>
    </PageLayout>
  );
};

export default EditCallRecord;
